import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Download, FileText, ShieldCheck } from 'lucide-react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { downloadDocumentPdf, useDocumentSends } from './hooks';
import { ListScreen, type ListTab } from '../../components/ListScreen';
import type { DocumentAuditItem, DocumentSendListItem, PackageSendStatus } from './types';

const STATUS_STYLES: Record<PackageSendStatus, string> = {
  Draft: 'bg-slate-100 text-slate-600',
  Sent: 'bg-slate-100 text-ink',
  Viewed: 'bg-slate-100 text-ink',
  PartiallySigned: 'bg-amber-100 text-amber-800',
  Signed: 'bg-green-100 text-green-700',
  Expired: 'bg-red-100 text-red-700',
  Revoked: 'bg-slate-200 text-slate-600'
};

const TABS: ListTab[] = [
  { key: 'all', label: 'All' },
  { key: 'awaiting', label: 'Awaiting signature' },
  { key: 'completed', label: 'Completed' },
  { key: 'expired', label: 'Expired' }
];

function inTab(status: PackageSendStatus, tab: string): boolean {
  if (tab === 'all') return true;
  if (tab === 'awaiting') return status === 'Sent' || status === 'Viewed' || status === 'PartiallySigned';
  if (tab === 'completed') return status === 'Signed';
  if (tab === 'expired') return status === 'Expired' || status === 'Revoked';
  return true;
}

function humanizeStatus(s: string): string {
  return s === 'PartiallySigned' ? 'Partially signed' : s === 'NotYetEffective' ? 'Not yet effective' : s;
}
function humanizeKind(kind: string): string {
  return { PowerOfAttorney: 'Power of attorney', VetAuthorization: 'Vet authorization', IncapacitationDirective: 'Incapacitation directive', Other: 'Other' }[kind] ?? kind;
}

export function SentDocumentsPage() {
  usePageTitle('Sent documents');
  const { data, isLoading, error } = useDocumentSends();
  const [tab, setTab] = useState('all');

  const rows = useMemo(
    () => (data ?? []).filter((s) => inTab(s.status, tab)).sort((a, b) => +new Date(b.sentAtUtc) - +new Date(a.sentAtUtc)),
    [data, tab]
  );

  return (
    <ListScreen
      title="Sent documents"
      tabs={TABS}
      activeTab={tab}
      onTabChange={setTab}
      secondaryAction={{ label: 'Validity report →', to: '/documents/validity' }}
      isEmpty={!isLoading && !error && rows.length === 0}
      empty={{
        icon: <FileText size={40} />,
        message: 'Nothing here yet.',
        action: <Link to="/documents/send" className="text-sm font-medium text-pine hover:text-pine-600">Send a document</Link>
      }}
    >
      {isLoading && <div className="px-4 py-6 text-sm text-slate-500">Loading…</div>}
      {error && <div className="px-4 py-6 text-sm text-danger">Could not load sent documents.</div>}
      {rows.map((send) => (
        <SendRow key={send.packageSendId} send={send} />
      ))}
    </ListScreen>
  );
}

function SendRow({ send }: { send: DocumentSendListItem }) {
  const [open, setOpen] = useState(false);
  const title = send.packageName ?? send.documents[0]?.templateName ?? 'Documents';

  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
        {open ? <ChevronDown size={16} className="shrink-0 text-slate-400" /> : <ChevronRight size={16} className="shrink-0 text-slate-400" />}
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium text-ink">{title}</span>
          <span className="mt-0.5 block truncate text-sm text-slate-500">
            {send.customerName} · {send.documents.length} doc{send.documents.length === 1 ? '' : 's'} · {new Date(send.sentAtUtc).toLocaleDateString()}
          </span>
        </span>
        <span className={`shrink-0 rounded-full px-2 py-1 text-xs font-medium ${STATUS_STYLES[send.status] ?? 'bg-slate-100 text-slate-600'}`}>
          {humanizeStatus(send.status)}
        </span>
      </button>
      {open && (
        <div className="divide-y divide-slate-100 border-t border-slate-100 bg-slate-50/50">
          {send.documents.map((doc) => (
            <DocRow key={doc.documentInstanceId} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
}

function DocRow({ doc }: { doc: DocumentAuditItem }) {
  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{doc.templateName}</div>
          <div className="text-xs text-slate-500">{humanizeKind(doc.kind)} · {humanizeStatus(doc.status)}</div>
        </div>
        <div className="flex shrink-0 items-center gap-3 text-sm">
          <a href={doc.verificationUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-pine hover:text-pine-600" title="QR verification page">
            <ShieldCheck size={15} /> Verify
          </a>
          <button type="button" onClick={() => downloadDocumentPdf(doc.downloadUrl, `${doc.templateName}.pdf`)} className="inline-flex items-center gap-1 text-pine hover:text-pine-600">
            <Download size={15} /> PDF
          </button>
        </div>
      </div>
      <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1 text-xs">
        {doc.signedAtUtc && (<><dt className="text-slate-500">Signed</dt><dd className="text-ink">{new Date(doc.signedAtUtc).toLocaleString()}</dd></>)}
        {doc.signerTypedName && (<><dt className="text-slate-500">Signer</dt><dd className="text-ink">{doc.signerTypedName}{doc.signerEmail ? ` · ${doc.signerEmail}` : ''}</dd></>)}
        {doc.signerIp && (<><dt className="text-slate-500">IP / agent</dt><dd className="break-words text-ink">{doc.signerIp} · <span className="text-slate-500">{doc.signerUserAgent}</span></dd></>)}
        <dt className="text-slate-500">SHA-256</dt><dd className="font-mono text-ink">{doc.documentSha256Prefix}</dd>
        <dt className="text-slate-500">Effective</dt>
        <dd className="text-ink">
          {doc.effectiveFromUtc
            ? `${new Date(doc.effectiveFromUtc).toLocaleDateString()} – ${new Date(doc.effectiveUntilUtc).toLocaleDateString()}`
            : `Until ${new Date(doc.effectiveUntilUtc).toLocaleDateString()}`}
        </dd>
      </dl>
    </div>
  );
}
